/* /app/frontend/src/pages/CommunicationCentrePage.jsx — iter-393
 *
 * Two-tab workspace for principals + managers:
 *
 *   1. CONTROL CENTRE (default) — portfolio-level Transaction Radar,
 *      dense per-deal table ordered by urgency, Critical Path per
 *      row, inline actions (Reply · Call · Request Document · Open
 *      Deal Room).  Reuses the existing Deal Room SSOT (blocker
 *      engine + resolved-blocker audit trail) via a new aggregation
 *      endpoint `GET /api/v2/control-centre`.
 *   2. EMAIL INBOX (preserved unchanged from iter-369A) — the office-
 *      wide inbox that surfaces which conversations need attention
 *      right now, matched to the correct Deal Room automatically.
 *
 * Both tabs share the same route (`/communication-centre`).  Tab
 * state is local so operators can switch without losing scroll /
 * filter position.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Inbox, MailQuestion, MailCheck, MailX, Search, MessageSquare,
  Link2, EyeOff, Building2, RefreshCw, LayoutDashboard, Mail,
  Send, Archive, ArchiveRestore, Sparkles, ExternalLink, CheckSquare, Square,
} from "lucide-react";
import { api } from "../lib/api";
import ControlCentreTab from "./communication/ControlCentreTab";
import MailReader from "./communication/MailReader";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import ShellCanvas from "../components/layout/ShellCanvas";
import { DemoBadge, isSimulatedDemo } from "../components/ui/DemoBadge";

const API = process.env.REACT_APP_BACKEND_URL;


const TABS = [
  { id: "all",          label: "All",          Icon: Inbox,        tone: "text-slate-700" },
  { id: "matched",      label: "Matched",      Icon: MailCheck,    tone: "text-emerald-700" },
  { id: "needs_review", label: "Needs Review", Icon: MailQuestion, tone: "text-amber-700" },
  { id: "unmatched",    label: "Unmatched",    Icon: MailX,        tone: "text-slate-500" },
  { id: "sent",         label: "Sent",         Icon: Send,         tone: "text-violet-700" },
  { id: "archived",     label: "Archived",     Icon: Archive,      tone: "text-slate-500" },
];


function _relativeTime(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const ms = Date.now() - t;
  if (ms < 60_000)     return "just now";
  if (ms < 3_600_000)  return `${Math.floor(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
  if (ms < 604_800_000) return `${Math.floor(ms / 86_400_000)}d`;
  return `${Math.floor(ms / 604_800_000)}w`;
}


export function BucketBadge({ bucket }) {
  const map = {
    matched:      { label: "Matched",      cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    needs_review: { label: "Needs Review", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
    unmatched:    { label: "Unmatched",    cls: "bg-slate-100 text-slate-600 ring-slate-200" },
  };
  const cfg = map[bucket] || map.unmatched;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.06em] ring-1 ${cfg.cls}`}
      data-testid={`comms-row-bucket-${bucket}`}
    >
      {cfg.label}
    </span>
  );
}


export function InboxRow({ message, selected, onToggleSelected, onLink, onDismiss, onOpen }) {
  const m = message;
  // iter-396 · flatten shape: real mailbox exposes match_bucket /
  // deal_id / deal_name at the top level.  Preserve back-compat
  // for any residual `match: { … }` shape by falling through.
  const bucket = m.match_bucket || m.match?.bucket || "unmatched";
  const dealId = m.deal_id || m.match?.deal_id || null;
  const dealName = m.deal_name || m.match?.deal_name || null;
  const score = (typeof m.match_confidence === "number") ? m.match_confidence : (m.match?.score ?? null);
  const isInbound = m.direction === "inbound";
  const fromName = m.from_name || (m.from_email ? m.from_email.split("@")[0] : "");
  const fromEmail = m.from_email || m.from?.email || "";
  const receivedAt = m.received_at || m.receivedAt;
  return (
    <li
      className={`grid grid-cols-[28px_minmax(0,220px)_minmax(0,1fr)_140px_84px_120px] gap-3 items-center px-4 py-3 rounded-lg hover:bg-slate-50 transition cursor-pointer min-w-0 ${m.unread ? "bg-white ring-1 ring-slate-200/60" : "bg-white/70"}`}
      data-testid={`comms-row-${m.id}`}
      data-bucket={bucket}
      onClick={() => onOpen?.(m)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(m); }
      }}
      role="button"
      tabIndex={0}
    >
      {/* Bulk select */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggleSelected?.(); }}
        className={`w-6 h-6 rounded-md ring-1 flex items-center justify-center transition ${selected ? "bg-violet-600 ring-violet-600 text-white" : "bg-white ring-slate-200 text-slate-400 hover:ring-slate-300"}`}
        aria-label={selected ? "Deselect" : "Select"}
        data-testid={`comms-row-select-${m.id}`}
        aria-pressed={selected}
      >
        {selected ? <CheckSquare size={12} strokeWidth={2.5} /> : <Square size={12} strokeWidth={2.2} />}
      </button>
      {/* Sender */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.unread && isInbound ? "bg-violet-500" : "bg-transparent"}`}
            aria-hidden
          />
          <span
            className="text-[13px] font-semibold text-slate-900 truncate"
            title={`${fromName} <${fromEmail}>`}
          >
            {isInbound ? fromName : `To · ${(m.to_emails || m.to || [])[0]?.email || (m.to_emails || m.to || [])[0] || fromName}`}
          </span>
        </div>
        <p className="mt-0.5 text-[11px] text-slate-500 truncate" title={fromEmail}>
          {fromEmail}
        </p>
      </div>
      {/* Subject + snippet */}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-[13px] font-medium text-slate-800 truncate" title={m.subject}>
            {m.subject || "(no subject)"}
          </p>
          {isSimulatedDemo(m) && <DemoBadge testid={`comms-row-demo-badge-${m.id}`} />}
        </div>
        <p className="mt-0.5 text-[11.5px] text-slate-500 truncate" title={m.snippet}>
          {m.snippet}
        </p>
      </div>
      {/* Deal (matched) or bucket */}
      <div className="min-w-0" data-testid={`comms-row-match-${m.id}`}>
        {dealId ? (
          <Link
            to={`/deal-rooms/${dealId}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-violet-700 hover:underline underline-offset-2 truncate"
            title={`Open ${dealName || "deal"}`}
            data-testid={`comms-row-deal-link-${m.id}`}
          >
            <Building2 size={11} strokeWidth={2.5} className="shrink-0" />
            <span className="truncate">{dealName || "Deal"}</span>
          </Link>
        ) : (
          <BucketBadge bucket={bucket} />
        )}
        {bucket === "matched" && score != null && (
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-emerald-700">
            {score}% confidence
          </p>
        )}
      </div>
      {/* Received */}
      <div className="text-right">
        <span className="text-[11.5px] font-semibold text-slate-600 tabular-nums" title={receivedAt}>
          {_relativeTime(receivedAt)}
        </span>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-end gap-1.5">
        {bucket !== "matched" && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onLink(m); }}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 ${
              bucket === "needs_review"
                ? "text-amber-800 bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-200"
                : "text-violet-700 hover:text-violet-800 hover:underline hover:bg-violet-50"
            }`}
            data-testid={`comms-row-link-${m.id}`}
            title="Match this email to a Deal Room"
          >
            <Link2 size={12} strokeWidth={2.5} />
            {bucket === "needs_review" ? "Match to deal" : "Link"}
          </button>
        )}
        {bucket !== "unmatched" && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDismiss(m); }}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-700 px-1.5 py-1 rounded-md hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            data-testid={`comms-row-dismiss-${m.id}`}
            title="Not a deal email"
          >
            <EyeOff size={12} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </li>
  );
}


function LinkDrawer({ message, deals, onClose, onConfirm }) {
  const [dealId, setDealId] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    // Reset picker state whenever the drawer opens on a new message.
    if (message) {
      setDealId("");
      setQuery("");
    }
  }, [message?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const filteredDeals = useMemo(() => {
    const list = deals || [];
    const q = query.trim().toLowerCase();
    if (!q) return list.slice(0, 100);
    return list
      .filter((d) => (d.name || "").toLowerCase().includes(q) || (d.address || "").toLowerCase().includes(q))
      .slice(0, 100);
  }, [deals, query]);
  if (!message) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40" data-testid="comms-link-drawer">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl p-5">
        <h3 className="text-[15px] font-bold text-slate-900">Match email to a Deal Room</h3>
        <p className="mt-1 text-[12.5px] text-slate-600 truncate" title={message.subject}>
          {message.subject}
        </p>
        <label className="block mt-4 text-[11.5px] uppercase tracking-[0.08em] font-bold text-slate-500">Search deals</label>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by address or reference…"
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
          data-testid="comms-link-search"
        />
        <div className="mt-3 max-h-64 overflow-auto rounded-lg ring-1 ring-slate-200" data-testid="comms-link-deal-list">
          {filteredDeals.length === 0 ? (
            <p className="px-3 py-4 text-center text-[12px] text-slate-500" data-testid="comms-link-empty">
              {deals.length === 0 ? "Loading deals…" : "No matching deals."}
            </p>
          ) : filteredDeals.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDealId(d.id)}
              className={`w-full text-left px-3 py-2 text-[12.5px] border-b border-slate-100 last:border-b-0 hover:bg-violet-50 ${dealId === d.id ? "bg-violet-100 text-violet-900 font-semibold" : "text-slate-700"}`}
              data-testid={`comms-link-deal-option-${d.id}`}
              aria-pressed={dealId === d.id}
            >
              <div className="truncate">{d.name || "(untitled deal)"}</div>
              {d.address && <div className="mt-0.5 text-[11px] text-slate-500 truncate">{d.address}</div>}
            </button>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[12.5px] font-semibold text-slate-600 hover:bg-slate-100 rounded-md" data-testid="comms-link-cancel">Cancel</button>
          <button type="button" onClick={() => onConfirm(dealId)} disabled={!dealId} className="px-3 py-1.5 text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-md disabled:opacity-50" data-testid="comms-link-confirm">Match</button>
        </div>
      </div>
    </div>
  );
}


/* ─── Email Inbox tab body — iter-396 · real Gmail mailbox ────
 *
 * Reads from `/api/v2/mailbox/inbox` (real Gmail mail after
 * OAuth · empty until the user connects).  Falls back to a
 * calm connect-prompt when the mailbox isn't linked, so
 * operators are never presented with mock data mixed with
 * live data.
 *
 * All iter-369A test-ids preserved verbatim so every historical
 * contract test continues to bind. */
function EmailInboxTab() {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ messages: [], counts: {}, provider_connected: false });
  const [loading, setLoading] = useState(true);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkDeals, setLinkDeals] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [readerTarget, setReaderTarget] = useState(null);

  const onOpen = useCallback((msg) => setReaderTarget(msg), []);
  const onReaderReadStateChanged = useCallback((id) => {
    setData((d) => ({
      ...d,
      messages: (d.messages || []).map((m) => m.id === id ? { ...m, unread: false } : m),
    }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tab });
      if (q.trim()) params.set("q", q.trim());
      const { data: body } = await api.get(`/api/v2/mailbox/inbox?${params}`);
      setData(body || { messages: [], counts: {}, provider_connected: false });
      setSelected(new Set());
    } catch (_) {
      setData({ messages: [], counts: {}, provider_connected: false });
    } finally {
      setLoading(false);
    }
  }, [tab, q]);

  useEffect(() => { load(); }, [load]);

  // iter-396A — populate the manual-match picker from the full deal
  // portfolio (`/api/v2/deals`) so operators can attach an email to
  // ANY deal, not just deals that already have matched emails.  Also
  // used for the Needs Review "Match to deal" flow.
  useEffect(() => {
    (async () => {
      try {
        const { data: rows } = await api.get(`/api/v2/deals`);
        const list = Array.isArray(rows) ? rows : [];
        setLinkDeals(list.map((d) => ({
          id: d.id,
          name: d.name || d.property_address || d.property?.address || "Deal",
          address: d.property_address || d.property?.address || null,
        })));
      } catch { /* fall through — picker will show "Loading deals…" */ }
    })();
  }, []);

  const onLink = useCallback((msg) => setLinkTarget(msg), []);
  const onDismiss = useCallback(async (msg) => {
    try {
      await api.post(`/api/v2/mailbox/${msg.id}/dismiss`);
    } finally { load(); }
  }, [load]);

  const onConfirmLink = useCallback(async (dealId) => {
    if (!linkTarget || !dealId) return;
    try {
      await api.post(`/api/v2/mailbox/${linkTarget.id}/link`, { deal_id: dealId });
    } finally {
      setLinkTarget(null);
      load();
    }
  }, [linkTarget, load]);

  const toggleSelected = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const bulkArchive = useCallback(async () => {
    if (!selected.size) return;
    await api.post("/api/v2/mailbox/bulk", { ids: [...selected], action: tab === "archived" ? "unarchive" : "archive" });
    load();
  }, [selected, load, tab]);

  const bulkRead = useCallback(async () => {
    if (!selected.size) return;
    await api.post("/api/v2/mailbox/bulk", { ids: [...selected], action: "read" });
    load();
  }, [selected, load]);

  const bulkDismiss = useCallback(async () => {
    if (!selected.size) return;
    await api.post("/api/v2/mailbox/bulk", { ids: [...selected], action: "dismiss" });
    load();
  }, [selected, load]);

  const providerConnected = !!data.provider_connected;

  return (
    <div data-testid="cc-email-inbox-tab" className="min-w-0 max-w-full">
      <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
        <div className="relative w-full sm:w-auto">
          <Search size={13} strokeWidth={2.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search sender, subject…"
            className="pl-7 pr-3 py-1.5 rounded-lg text-[12.5px] border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-violet-300 w-full sm:w-auto sm:min-w-[220px]"
            data-testid="comms-search-input"
          />
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
          data-testid="comms-refresh"
          title="Refresh inbox"
        >
          <RefreshCw size={12} strokeWidth={2.5} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Provider banner — reflects real connection state */}
      <div
        className={`mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-[11.5px] ring-1 ${
          providerConnected
            ? "bg-emerald-50 ring-emerald-200/70 text-emerald-800"
            : "bg-violet-50 ring-violet-200/70 text-violet-800"
        }`}
        data-testid="comms-provider-banner"
        data-provider-connected={providerConnected}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${providerConnected ? "bg-emerald-500" : "bg-violet-500"}`} />
        {providerConnected ? (
          <span>
            <b>Live Gmail mailbox</b> · syncing every 5 minutes.  New emails match to Deal Rooms automatically; low-confidence ones land in <b>Needs Review</b>.
          </span>
        ) : (
          <span className="flex-1">
            <b>No mailbox connected.</b>  Head to <Link to="/settings/email" className="underline underline-offset-2 font-semibold hover:text-violet-900" data-testid="comms-connect-link">Settings → Email</Link> to link your Gmail — new mail flows here automatically once you're signed in.
          </span>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="comms-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          const count = data.counts?.[t.id] ?? 0;
          const Icon = t.Icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 -mb-px border-b-2 text-[12.5px] font-semibold whitespace-nowrap transition ${
                active
                  ? "border-violet-500 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`comms-tab-${t.id}`}
              aria-selected={active}
            >
              <Icon size={13} strokeWidth={2.5} className={active ? t.tone : ""} />
              {t.label}
              <span className={`ml-1 tabular-nums text-[11px] font-bold ${active ? "text-slate-800" : "text-slate-400"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Bulk toolbar */}
      {selected.size > 0 && (
        <div
          className="mt-3 flex items-center gap-2 rounded-lg bg-slate-100 ring-1 ring-slate-200 px-3 py-2 text-[11.5px] text-slate-700"
          data-testid="comms-bulk-toolbar"
        >
          <span className="font-semibold tabular-nums" data-testid="comms-bulk-selected-count">{selected.size} selected</span>
          <button type="button" onClick={bulkRead} className="ml-2 inline-flex items-center gap-1 h-6 px-2 rounded-md bg-white ring-1 ring-slate-200 text-[11px] font-semibold hover:bg-slate-50" data-testid="comms-bulk-read">
            <MailCheck size={11} strokeWidth={2.4} /> Mark read
          </button>
          <button type="button" onClick={bulkArchive} className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-white ring-1 ring-slate-200 text-[11px] font-semibold hover:bg-slate-50" data-testid="comms-bulk-archive">
            {tab === "archived" ? <ArchiveRestore size={11} strokeWidth={2.4} /> : <Archive size={11} strokeWidth={2.4} />}
            {tab === "archived" ? "Unarchive" : "Archive"}
          </button>
          <button type="button" onClick={bulkDismiss} className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-white ring-1 ring-slate-200 text-[11px] font-semibold hover:bg-slate-50" data-testid="comms-bulk-dismiss">
            <EyeOff size={11} strokeWidth={2.4} /> Not a deal
          </button>
          <span className="flex-1" />
          <button type="button" onClick={() => setSelected(new Set())} className="text-[11px] text-slate-500 hover:text-slate-700" data-testid="comms-bulk-clear">Clear</button>
        </div>
      )}

      {/* List */}
      <ul className="mt-4 space-y-2" data-testid="comms-list">
        {loading && (
          <li className="rounded-lg bg-slate-50 px-4 py-6 text-center text-[12.5px] text-slate-500" data-testid="comms-loading">
            Loading inbox…
          </li>
        )}
        {!loading && data.messages.length === 0 && !providerConnected && (
          <li className="rounded-2xl bg-gradient-to-br from-violet-50 to-white ring-1 ring-violet-200/60 px-6 py-12 text-center" data-testid="comms-connect-hero">
            <Sparkles size={22} className="mx-auto text-violet-500 mb-3" strokeWidth={2} />
            <p className="text-[14px] font-semibold text-slate-900">Bring your transaction emails into ChainFlow</p>
            <p className="mt-1 text-[12.5px] text-slate-600 max-w-md mx-auto">
              Once you connect Gmail, every property email will land in the right Deal Room automatically.  Low-confidence matches land in Needs Review for a one-click confirm.
            </p>
            <Link
              to="/settings/email"
              className="mt-4 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-violet-600 text-white text-[12.5px] font-semibold hover:bg-violet-700"
              data-testid="comms-connect-cta"
            >
              <Mail size={13} strokeWidth={2.5} /> Connect Gmail <ExternalLink size={11} strokeWidth={2.4} className="opacity-80" />
            </Link>
          </li>
        )}
        {!loading && data.messages.length === 0 && providerConnected && (
          <li className="rounded-lg bg-slate-50 px-4 py-10 text-center text-[13px] text-slate-500" data-testid="comms-empty">
            <Inbox size={24} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
            No conversations in this tab.
          </li>
        )}
        {!loading && data.messages.map((m) => (
          <InboxRow
            key={m.id}
            message={m}
            selected={selected.has(m.id)}
            onToggleSelected={() => toggleSelected(m.id)}
            onLink={onLink}
            onDismiss={onDismiss}
            onOpen={onOpen}
          />
        ))}
      </ul>

      <LinkDrawer
        message={linkTarget}
        deals={linkDeals}
        onClose={() => setLinkTarget(null)}
        onConfirm={onConfirmLink}
      />

      <MailReader
        message={readerTarget}
        onClose={() => setReaderTarget(null)}
        onReadStateChanged={onReaderReadStateChanged}
      />
    </div>
  );
}


/* ─── Root ────────────────────────────────────────────────────── */
const _WORKSPACE_TABS = [
  { id: "control", label: "Portfolio", Icon: LayoutDashboard, testid: "cc-workspace-tab-control" },
  { id: "inbox",   label: "Email Inbox",    Icon: Mail,             testid: "cc-workspace-tab-inbox" },
];

// Decorative header: soft cyan → blue → purple flowing curves that fade into white.
function CommsHeaderArt() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 h-[280px] -z-10 overflow-hidden"
      style={{ WebkitMaskImage: "linear-gradient(to bottom, #000 45%, transparent)", maskImage: "linear-gradient(to bottom, #000 45%, transparent)" }}
      data-testid="comms-header-art"
    >
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 260" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="cfCommsWave" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#67E8F9" />
            <stop offset="50%" stopColor="#60A5FA" />
            <stop offset="100%" stopColor="#A78BFA" />
          </linearGradient>
        </defs>
        <rect width="1440" height="260" fill="url(#cfCommsWave)" opacity="0.07" />
        <path d="M0 150 C 260 70, 520 210, 820 120 S 1260 40, 1440 110 L1440 0 L0 0 Z" fill="url(#cfCommsWave)" opacity="0.16" />
        <path d="M0 100 C 300 30, 600 170, 900 80 S 1300 10, 1440 60" fill="none" stroke="url(#cfCommsWave)" strokeWidth="2" opacity="0.35" />
        <path d="M0 170 C 340 110, 640 230, 960 140 S 1320 90, 1440 140" fill="none" stroke="url(#cfCommsWave)" strokeWidth="1.5" opacity="0.25" />
      </svg>
    </div>
  );
}

export default function CommunicationCentrePage() {
  const [workspaceTab, setWorkspaceTab] = useState("control");

  // iter-alignment · Communication Centre · Sidebar Offset Fix
  // (2026-03-09) — Every primary workspace page renders inside a
  // `<ShellCanvas>` so its content sits to the right of the fixed
  // sidebar (`md:ml-[var(--cf-sidebar-w,300px)]`).  Communication
  // Centre was the only page still consuming `WORKSPACE_SHELL_CLASS`
  // directly on its outer <div>, which meant its heading, filters
  // and first table column rendered *underneath* the sidebar on
  // md+ viewports.  Wrapping in `<ShellCanvas>` restores the
  // canonical offset without touching the page's inner layout.
  return (
    <ShellCanvas data-testid="communication-centre-shell">
      <div className="relative isolate min-w-0 overflow-x-clip">
      <CommsHeaderArt />
      <div className={`${WORKSPACE_SHELL_CLASS} min-w-0 box-border`} data-testid="communication-centre-page">
      <PageHeader
        icon={MessageSquare}
        title="Communication Centre"
        description="See where communication is holding deals up — and take action."
        testid="communication-header"
        titleTestid="comms-heading"
      />

      {/* Workspace tab switch.
       *
       * iter-alignment · P0 · Communication Header Alignment Fix
       * (2026-03-01) — The shared <PageHeader> already carries an
       * `mb-6` so the tab strip must sit at `mt-0` to inherit the
       * same 24px header-to-content rhythm Deal Rooms uses.  The
       * previous `mt-5` stacked with `mb-6` and pushed the tabs
       * ~44px below the description. */}
      <div
        className="inline-flex items-center rounded-xl bg-slate-100 p-1 ring-1 ring-slate-200/60"
        data-testid="cc-workspace-tabs"
        role="tablist"
      >
        {_WORKSPACE_TABS.map((t) => {
          const active = workspaceTab === t.id;
          const Icon = t.Icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setWorkspaceTab(t.id)}
              className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-lg text-[12.5px] font-semibold transition ${
                active
                  ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/70"
                  : "text-slate-600 hover:text-slate-800"
              }`}
              data-testid={t.testid}
              role="tab"
              aria-selected={active}
            >
              <Icon size={13} strokeWidth={2.4} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {workspaceTab === "control" ? <ControlCentreTab /> : <EmailInboxTab />}
      </div>
      </div>
      </div>
    </ShellCanvas>
  );
}
